import type { PostRecord } from "@/types/api";
import { firstHashtag } from "@/lib/posts-helpers";
import { hashtagSlug } from "@/lib/seo/hashtag-slug";
import { imageCaptionsEnabled } from "@/lib/seo/image-caption-flag";
import { isFlaggedTag } from "@/lib/seo/content-policy";

/**
 * One caption per sticker image, used for BOTH the `<img alt>` and the
 * `ImageObject` `caption`/`name` on the same page, so the two can never drift.
 *
 * WHY THIS IS WHERE THE "TITLE" LIVES
 *
 * Image sitemaps no longer carry titles: Google deprecated `<image:title>` and
 * `<image:caption>` in 2022 and reads only `<image:loc>`. Alt text and ImageObject
 * are the places it still reads a description from, so this is the one source
 * for both. The sitemap (generated by sticko-api-v2) is discovery only.
 *
 * SHAPE
 *
 *   "<pack title> – <tag> sticker <n>"     e.g. "Happy Diwali Stickers – diya sticker 3"
 *   "<pack title> sticker <n>"             when there is no usable tag
 *
 *  - The pack title leads, unchanged: it is the token these pages already rank
 *    on, and this only ever appends to it.
 *  - The tag is `firstHashtag()` — the same tag the breadcrumb parent uses — so
 *    the alt, the breadcrumb and the /hashtag/ link all name one thing. It is
 *    normalised with `hashtagSlug()` to match the URL it links to.
 *  - No "WhatsApp". The title, h1 and intro already say it, and ten alts on a
 *    page repeating a keyword is exactly the stuffing Google's image guidance
 *    warns against. Same reason for the stoplist: "sticker", "stickers" and
 *    "animated" are already in the surrounding text of every pack.
 *  - The tag is dropped when it would repeat the title ("Love Stickers – love"),
 *    when it is on the stoplist, when it is implausibly long, or when the whole
 *    caption would run past CAPTION_MAX_LENGTH — a caption that gets truncated in
 *    a screen reader or an image result is worse than a shorter one.
 *
 * NUMBERING
 *
 * `n` is 1-based and must come from the sticker's position in `sticker_Id`
 * order — see `orderByStickerId()` — not from wherever the API happened to put
 * it in an array. The detail page renders the first ten by sticker_Id and the
 * image sitemap lists the same ten in the same order, so "sticker 3" names one
 * specific image everywhere it appears, across renders and across generators.
 * There is no per-sticker text in the data to do better with: the `Emojis`
 * column is empty on every row sampled and `sticker_tags` is usually "animated".
 *
 * ROLLBACK
 *
 * NEXT_PUBLIC_IMAGE_CAPTIONS=off restores the pre-existing text BYTE-FOR-BYTE — including
 * its warts. The legacy form was the template literal `${post_title} sticker N`
 * with no trimming, so a padded title kept its padding and a null title printed
 * "null". The rollback promise is "what shipped before", not "what should have
 * shipped", so the legacy path reproduces exactly that and touches nothing.
 */
export const CAPTION_MAX_LENGTH = 80;

/** Words that add nothing beside a sticker on a page about stickers. */
export const CAPTION_STOPLIST: ReadonlySet<string> = new Set([
  "whatsapp",
  "sticker",
  "stickers",
  "animated",
]);

/** A tag longer than this is a sentence someone typed into the tag box, not a tag. */
const MAX_TAG_LENGTH = 30;

type CaptionRecord = Pick<PostRecord, "post_title" | "post_description" | "post_tags">;

/** The tag worth naming beside a sticker of this pack, or null. Pure. */
export function captionTagFor(record: CaptionRecord): string | null {
  const raw = firstHashtag(record);
  if (!raw) return null;
  const tag = hashtagSlug(raw);
  if (!tag || tag.length > MAX_TAG_LENGTH) return null;
  if (CAPTION_STOPLIST.has(tag)) return null;
  // Never write an adult, PII or spam tag into Sticko's own alt text — the
  // production love grid read "– sexy sticker 1". Pack titles are untouched.
  if (isFlaggedTag(tag)) return null;
  if (
    String(record.post_title ?? "")
      .toLowerCase()
      .includes(tag)
  )
    return null;
  return tag;
}

/**
 * The pre-flag form, byte-identical to the template literal the pages have
 * always emitted. Deliberately not trimmed and deliberately tolerant of a null
 * title (it prints "null", exactly as `${post.post_title}` did) — see ROLLBACK.
 */
export function legacyStickerCaption(title: string | null | undefined, n: number): string {
  return `${title} sticker ${n}`;
}

/** Builds the descriptive form from already-decided parts. Pure. */
export function buildStickerCaption(
  title: string | null | undefined,
  tag: string | null,
  n: number,
): string {
  const clean = String(title ?? "").trim();
  const plain = `${clean} sticker ${n}`;
  if (!tag) return plain;
  const withTag = `${clean} – ${tag} sticker ${n}`;
  return withTag.length <= CAPTION_MAX_LENGTH ? withTag : plain;
}

/**
 * The caption function for one pack. Resolves the flag and the tag ONCE and
 * returns a closure over the number, so a card with ten stickers derives the
 * tag once rather than ten times, and the alt text and the ImageObject on the
 * same page come from the same closure rather than from two matching calls.
 */
export function stickerCaptioner(record: CaptionRecord): (n: number) => string {
  if (!imageCaptionsEnabled()) return (n) => legacyStickerCaption(record.post_title, n);
  const title = record.post_title;
  const tag = captionTagFor(record);
  return (n) => buildStickerCaption(title, tag, n);
}

/** One-off form of `stickerCaptioner` for a single sticker. */
export function stickerImageCaption(record: CaptionRecord, n: number): string {
  return stickerCaptioner(record)(n);
}

/**
 * The cover's caption — and the og:image alt, which is the same string. Not
 * trimmed: this is the literal the pages have always shipped, kept byte-for-byte
 * so the cover's ImageObject and og:image alt do not change under anyone.
 */
export function packCoverCaption(title: string | null | undefined): string {
  return `${title} sticker pack cover`;
}

/**
 * Stickers in `sticker_Id` order, which is the order the detail page renders and
 * the image sitemap lists. Never mutates its input.
 *
 * Deterministic even on odd payloads: ids are compared numerically, a sticker
 * whose id is missing or non-numeric sorts after every numeric one, and ties
 * keep their input order — a comparator that returned NaN would let the engine
 * place such rows anywhere, and "sticker 3" would stop meaning one image.
 */
export function orderByStickerId<T extends { sticker_Id?: number | string | null }>(
  stickers: readonly T[],
): T[] {
  return stickers
    .map((sticker, index) => ({ sticker, index, id: Number(sticker.sticker_Id) }))
    .sort((a, b) => {
      const an = Number.isFinite(a.id);
      const bn = Number.isFinite(b.id);
      if (an && bn) return a.id - b.id || a.index - b.index;
      if (an) return -1;
      if (bn) return 1;
      return a.index - b.index;
    })
    .map((entry) => entry.sticker);
}
